import { useEffect, useState, type FormEvent } from 'react'
import {
  ApiError,
  getBookingPage,
  getEvents,
  getLocations,
  signUp,
  type BookingPage,
  type EventCard,
  type LocationOption,
} from '../api'
import { FALLBACK_TZ, formatHHmm, todayIso } from '../panel/lib/time'
import { bookResource, getResource, getSlots, type ResourceCard, type Slot } from '../resource/api'
import { SignupForm } from '../SignupForm'
import { DayStrip } from '../DayStrip'

type Item =
  | { kind: 'event'; startTime: string; endTime: string; event: EventCard }
  | { kind: 'slot'; startTime: string; endTime: string; resource: ResourceCard; slot: Slot }

/** `?rooms=1,2` → [1, 2]; мусор в ссылке молча отбрасываем. */
const idsParam = (name: string): number[] =>
  (new URLSearchParams(window.location.search).get(name) ?? '')
    .split(',')
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0)

const errorText = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.message : fallback

/** `/a?rooms=1,2&resources=126,127` — состав прямо в ссылке. */
export function ActivitiesByQuery() {
  const [roomIds] = useState(() => idsParam('rooms'))
  const [resourceIds] = useState(() => idsParam('resources'))
  return <ActivitiesPage roomIds={roomIds} resourceIds={resourceIds} />
}

/** `/a/<code>` — состав сохранён в админке, ссылка не меняется при правках. */
export function ActivitiesByCode({ code }: { code: string }) {
  const [page, setPage] = useState<BookingPage | null>(null)
  const [missing, setMissing] = useState(false)

  useEffect(() => {
    getBookingPage(code)
      .then((p) => {
        setPage(p)
        document.title = p.name
      })
      .catch(() => setMissing(true))
  }, [code])

  if (missing) {
    return (
      <main className="card card--wide">
        <h1>Не получилось</h1>
        <p className="muted">Страница не найдена — проверьте ссылку</p>
      </main>
    )
  }
  if (!page) {
    return (
      <main className="card card--wide">
        <p className="muted">Загружаем…</p>
      </main>
    )
  }
  return <ActivitiesPage roomIds={page.roomIds} resourceIds={page.resourceIds} title={page.name} />
}

/**
 * Сборная страница, как активности yclients: мероприятия переговорок и
 * свободные слоты ресурсов за выбранный день одной лентой; запись — на любой пункт.
 */
function ActivitiesPage({
  roomIds,
  resourceIds,
  title: pageTitle = 'Расписание',
}: {
  roomIds: number[]
  resourceIds: number[]
  title?: string
}) {
  const [resources, setResources] = useState<ResourceCard[] | null>(null)
  const [locations, setLocations] = useState<LocationOption[]>([])
  const [date, setDate] = useState('')
  const [items, setItems] = useState<Item[] | null>(null)
  const [picked, setPicked] = useState<Item | null>(null)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reload, setReload] = useState(0)

  // Часовой пояс — по первому ресурсу: сборная страница живёт в одной локации.
  const tz = resources?.[0]?.location?.timezone ?? FALLBACK_TZ

  useEffect(() => {
    Promise.all(resourceIds.map((id) => getResource(id).catch(() => null))).then((cards) => {
      const open = cards.filter((c): c is ResourceCard => c !== null && c.isAvailable)
      setResources(open)
      setDate(todayIso(open[0]?.location?.timezone ?? FALLBACK_TZ))
    })
    if (roomIds.length) getLocations().then(setLocations).catch(() => setLocations([]))
  }, [roomIds, resourceIds])

  useEffect(() => {
    if (!date || !resources) return
    // Ответ за прошлую дату может прийти позже — он не должен затереть текущую.
    let stale = false
    setItems(null)
    setError(null)
    Promise.all([
      roomIds.length ? getEvents(roomIds, date) : Promise.resolve([]),
      ...resources.map((r) =>
        // Один упавший ресурс не должен гасить всю ленту.
        getSlots(r.id, date).catch(() => []).then((slots) =>
          slots
            .filter((s) => s.available)
            .map((slot): Item => ({ kind: 'slot', ...slot, resource: r, slot })),
        ),
      ),
    ])
      .then(([events, ...slotLists]) => {
        if (stale) return
        const now = Date.now()
        const eventItems = (events as EventCard[])
          .filter((e) => e.seatsLeft === null || e.seatsLeft > 0)
          .map((event): Item => ({ kind: 'event', ...event, event }))
        setItems(
          [...eventItems, ...(slotLists as Item[][]).flat()]
            .filter((i) => new Date(i.startTime).getTime() > now)
            .sort((a, b) => a.startTime.localeCompare(b.startTime)),
        )
      })
      .catch((err: unknown) => {
        if (stale) return
        setItems([])
        setError(errorText(err, 'Не удалось загрузить расписание'))
      })
    return () => {
      stale = true
    }
  }, [date, resources, roomIds, reload])

  if (!roomIds.length && !resourceIds.length) {
    return (
      <main className="card card--wide">
        <h1>Не получилось</h1>
        <p className="muted">В ссылке не выбрано ни одного зала или ресурса</p>
      </main>
    )
  }

  const time = (iso: string) => formatHHmm(new Date(iso), tz)
  const minutes = (i: Item) =>
    Math.round((new Date(i.endTime).getTime() - new Date(i.startTime).getTime()) / 60000)
  const title = (i: Item) =>
    i.kind === 'event' ? (i.event.title ?? 'Мероприятие') : i.resource.name
  const place = (i: Item) => (i.kind === 'event' ? i.event.room.name : i.resource.location?.name)
  const seatsLeft = (i: Item) => (i.kind === 'event' ? i.event.seatsLeft : i.slot.seatsFree)
  const back = () => {
    setPicked(null)
    setDone(false)
    setReload((n) => n + 1)
  }

  if (picked) {
    const day = new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      timeZone: tz,
    }).format(new Date(picked.startTime))
    return (
      <main className="card card--wide">
        <h1>{title(picked)}</h1>
        <p className="when">
          {day}, {time(picked.startTime)}–{time(picked.endTime)}
        </p>
        {place(picked) && <p className="muted">{place(picked)}</p>}
        {picked.kind === 'event' && picked.event.description && (
          <p className="description">{picked.event.description}</p>
        )}
        {done ? (
          <p className="done">Вы записаны. До встречи!</p>
        ) : picked.kind === 'event' ? (
          <SignupForm
            locations={locations}
            onSubmit={async (payload) => {
              await signUp(picked.event.uuid, payload)
              setDone(true)
            }}
          />
        ) : (
          <SlotForm
            onSubmit={(clientName, clientPhone) =>
              bookResource({
                roomId: picked.resource.id,
                startTime: picked.startTime,
                endTime: picked.endTime,
                clientName,
                clientPhone,
              }).then(() => setDone(true))
            }
          />
        )}
        <button type="button" className="back" onClick={back}>
          ← К расписанию
        </button>
      </main>
    )
  }

  return (
    <main className="card card--wide">
      <h1>{pageTitle}</h1>
      {date && resources && (
        <DayStrip today={todayIso(tz)} value={date} onChange={setDate} maxWeeks={2} />
      )}

      {error ? (
        <p className="error">{error}</p>
      ) : items === null ? (
        <p className="muted">Загружаем…</p>
      ) : items.length === 0 ? (
        <p className="muted">На этот день записи нет</p>
      ) : (
        <ul className="activities">
          {items.map((i) => (
            <li key={`${i.kind}-${i.kind === 'event' ? i.event.uuid : i.resource.id}-${i.startTime}`}>
              <button type="button" className="activity" onClick={() => setPicked(i)}>
                <span className="activity__time">{time(i.startTime)}</span>
                <span className="activity__body">
                  <strong>{title(i)}</strong>
                  <small>
                    {minutes(i)} мин{place(i) ? ` · ${place(i)}` : ''}
                    {seatsLeft(i) !== null && ` · мест: ${seatsLeft(i)}`}
                  </small>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </main>
  )
}

/** Имя и телефон для слота ресурса — те же поля, что на `/r/<id>`. */
function SlotForm({ onSubmit }: { onSubmit: (name: string, phone: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    setBusy(true)
    setError(null)
    try {
      await onSubmit(
        String(form.get('name') ?? '').trim(),
        String(form.get('phone') ?? '').trim(),
      )
    } catch (err: unknown) {
      setError(errorText(err, 'Не удалось записаться'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <label>
        Имя <span className="required">*</span>
        <input name="name" required autoComplete="name" />
      </label>
      <label>
        Телефон <span className="required">*</span>
        <input name="phone" type="tel" required placeholder="+7 999 111-22-33" autoComplete="tel" />
      </label>
      {error && <p className="error">{error}</p>}
      <button type="submit" disabled={busy}>
        {busy ? 'Записываем…' : 'Записаться'}
      </button>
    </form>
  )
}
